import React, { useState, useCallback, useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Edit from "@material-ui/icons/Edit";
import WhatsApp from "@material-ui/icons/WhatsApp";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";
import RowActionsMenu from "../../components/RowActionsMenu";
import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    gap: theme.spacing(2),
    minWidth: 0,
  },
  mainPaper: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    padding: 0,
    borderRadius: 18,
    overflow: "hidden",
    ...theme.scrollbarStyles,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(8,145,178,0.26) 100%)"
        : "linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(224,242,254,0.96) 100%)",
    border: `1px solid ${theme.palette.divider}`,
  },
  heroActions: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  summaryCard: {
    padding: theme.spacing(2),
    borderRadius: 16,
    minHeight: 104,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  summaryLabel: {
    fontSize: "0.82rem",
    opacity: 0.78,
  },
  summaryValue: {
    fontSize: "1.9rem",
    fontWeight: 700,
    lineHeight: 1.1,
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 18,
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  tableHead: {
    backgroundColor: theme.palette.barraSuperior,
    "& .MuiTableCell-root": {
      color: "#fff",
      fontWeight: 600,
    },
  },
  rowName: {
    fontWeight: 600,
  },
  statusStack: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "center",
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  mobileSection: {
    padding: theme.spacing(1.5),
  },
  mobileCards: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: theme.spacing(1.5),
  },
  mobileCard: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5),
    boxShadow: "none",
  },
  mobileCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  mobileCardBody: {
    display: "grid",
    gap: theme.spacing(1),
  },
  mobileMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  mobileMetaLabel: {
    fontSize: "0.78rem",
    opacity: 0.65,
  },
}));

const AllConnections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { list } = useCompanies();

  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);
  const [whats, setWhats] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (!user.super) {
      history.push("/tickets");
    }
  }, [history, user?.id, user?.super]);

  useEffect(() => {
    setLoadingWhatsapp(true);

    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingWhatsapp(false);
      }
    };

    fetchSession();
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoadingComp(true);
    try {
      const companyList = await list();
      setCompanies(companyList);
    } catch (err) {
      toast.error("Nao foi possivel carregar a lista de empresas");
    } finally {
      setLoadingComp(false);
    }
  }, [list]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleOpenWhatsAppModal = (whatsappsFilter = [], comp = null) => {
    setWhatsAppModalOpen(true);
    setFilterConnections(whatsappsFilter);
    setCompanyWhatsApps(comp);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, []);

  const companyRows = useMemo(() => {
    const normalizedSearch = searchParam.trim().toLowerCase();

    const rows = companies.map((company) => {
      const companyConnections = whats.filter(
        (item) => item?.companyId === company?.id
      );
      const connected = companyConnections.filter(
        (item) => item?.status === "CONNECTED"
      ).length;
      const disconnected = companyConnections.length - connected;

      return {
        company,
        connected,
        disconnected,
        total: companyConnections.length,
        companyConnections,
      };
    });

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter(({ company }) =>
      String(company?.name || "")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [companies, searchParam, whats]);

  const summary = useMemo(() => {
    const total = whats.length;
    const connected = whats.filter((item) => item?.status === "CONNECTED").length;
    const disconnected = total - connected;
    const withProblems = companyRows.filter(
      (row) => row.total > 0 && row.disconnected > 0
    ).length;

    return {
      companies: companyRows.length,
      total,
      connected,
      disconnected,
      withProblems,
    };
  }, [companyRows, whats]);

  const summaryCards = useMemo(
    () => [
      { key: "companies", label: "Empresas visiveis", value: summary.companies },
      { key: "connected", label: "Conexoes conectadas", value: summary.connected },
      { key: "disconnected", label: "Conexoes desconectadas", value: summary.disconnected },
      { key: "total", label: "Total monitorado", value: summary.total },
    ],
    [summary]
  );

  if (user?.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer className={classes.mainContainer}>
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={null}
      />

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.mobileSection}>
          <Paper className={classes.hero} elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" style={{ fontWeight: 700 }} gutterBottom>
                  {i18n.t("connections.title")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Acompanhe a saude das conexoes por empresa e ajuste rapidamente os canais ativos da operacao.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={classes.heroActions}>
                  <PopupState variant="popover" popupId="connections-channel-menu">
                    {(popupState) => (
                      <React.Fragment>
                        <Button
                          variant="contained"
                          color="primary"
                          {...bindTrigger(popupState)}
                        >
                          Nova conexao
                        </Button>

                        <Menu {...bindMenu(popupState)}>
                          <MenuItem
                            onClick={() => {
                              handleOpenWhatsAppModal();
                              popupState.close();
                            }}
                          >
                            <WhatsApp fontSize="small" style={{ marginRight: 10 }} />
                            WhatsApp
                          </MenuItem>

                        </Menu>
                      </React.Fragment>
                    )}
                  </PopupState>
                </div>
              </Grid>
            </Grid>
          </Paper>

          <MetricsStrip ariaLabel="Indicadores de conexoes por empresa" items={summaryCards} />

          <Paper className={classes.tablePaper} variant="outlined">
            <Box className={classes.tableHeader}>
              <Typography variant="body1" className={classes.tableHeaderTitle}>
                Conexoes por empresa
              </Typography>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder="Buscar empresa"
                type="search"
                value={searchParam}
                onChange={(event) => setSearchParam(event.target.value)}
                inputProps={{ "aria-label": "Pesquisar empresas" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {loadingWhatsapp || loadingComp ? (
              <Box p={2}>
                <TableRowSkeleton columns={5} />
              </Box>
            ) : isMobile ? (
              <Box className={classes.mobileSection}>
                <div className={classes.mobileCards}>
                  {companyRows.map((row) => (
                    <Paper key={row.company.id} className={classes.mobileCard}>
                      <div className={classes.mobileCardHeader}>
                        <div>
                          <Typography variant="body1" className={classes.rowName}>
                            {row.company?.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {row.total > 0
                              ? `${row.total} conexoes monitoradas`
                              : "Nenhuma conexao cadastrada"}
                          </Typography>
                        </div>

                        <RowActionsMenu
                          buttonTitle={`Acoes da empresa ${row.company?.name}`}
                          actions={[
                            {
                              key: "edit",
                              label: "Gerenciar conexoes",
                              icon: Edit,
                              onClick: () =>
                                handleOpenWhatsAppModal(
                                  row.companyConnections,
                                  row.company
                                ),
                            },
                          ]}
                        />
                      </div>

                      <div className={classes.mobileCardBody}>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Conectadas
                          </Typography>
                          <Chip
                            size="small"
                            label={row.connected}
                            style={{ backgroundColor: "#dcfce7", color: "#166534" }}
                          />
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Desconectadas
                          </Typography>
                          <Chip
                            size="small"
                            label={row.disconnected}
                            style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
                          />
                        </div>
                        <div className={classes.mobileMetaRow}>
                          <Typography className={classes.mobileMetaLabel}>
                            Total
                          </Typography>
                          <Chip
                            size="small"
                            label={row.total}
                            style={{ backgroundColor: "#e0f2fe", color: "#075985" }}
                          />
                        </div>
                      </div>
                    </Paper>
                  ))}
                </div>
              </Box>
            ) : (
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell align="center">Cliente</TableCell>
                      <TableCell align="center">Conexoes conectadas</TableCell>
                      <TableCell align="center">Conexoes desconectadas</TableCell>
                      <TableCell align="center">Total de conexoes</TableCell>
                      <TableCell align="center" className={classes.actionsCell}>
                        {i18n.t("connections.table.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyRows.map((row) => (
                      <TableRow key={row.company.id} hover>
                        <TableCell align="center">
                          <Typography variant="body2" className={classes.rowName}>
                            {row.company?.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.connected}
                            style={{ backgroundColor: "#dcfce7", color: "#166534" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.disconnected}
                            style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.total}
                            style={{ backgroundColor: "#e0f2fe", color: "#075985" }}
                          />
                        </TableCell>
                        <TableCell align="center" className={classes.actionsCell}>
                          <RowActionsMenu
                            buttonTitle={`Acoes da empresa ${row.company?.name}`}
                            actions={[
                              {
                                key: "edit",
                                label: "Gerenciar conexoes",
                                icon: Edit,
                                onClick: () =>
                                  handleOpenWhatsAppModal(
                                    row.companyConnections,
                                    row.company
                                  ),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow className={classes.tableHead}>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">{summary.connected}</TableCell>
                      <TableCell align="center">{summary.disconnected}</TableCell>
                      <TableCell align="center">{summary.total}</TableCell>
                      <TableCell align="center" />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loadingWhatsapp && !loadingComp && companyRows.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography variant="h6">Nenhuma empresa encontrada</Typography>
                <Typography variant="body2" className={classes.helperText}>
                  Ajuste a busca para localizar a operacao desejada.
                </Typography>
              </Box>
            )}
          </Paper>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default AllConnections;
